import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import "./register.scss";
import { BrandButton } from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import Loader from "../components/loader";
import LoadError from "../components/error";
import { ApiEndpoint } from "../components/utils";
import { z } from "zod";
import { Alert, Snackbar } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { pageIds } from "../components/utils";
import BackButton from "../components/backBtn";

interface PageResponse {
  headerTitle?: string;
  welcomeMessage?: any;
}

const Register = () => {
  const [data, setdata] = useState<PageResponse | null>();
  const [snackError, setSnackError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [checking, setChecking] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");
  const vertical = "bottom";
  const horizontal = "center";
  const notEmptyString = z.string().min(1);
  const validEmail = z.object({
    email: z
      .string()
      .min(1, { message: "Email field has to be filled." })
      .refine(
        (val) =>
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val ?? ""),
        "invalid email",
      ),
  });
  const validPhone = z
    .string()
    .min(1, { message: "Phone field has to be filled." })
    .refine((val) => /^\d+$/.test(val ?? ""), "Phone must contain digits only");
  const email = useRef("");
  const firstName = useRef("");
  const lastName = useRef("");
  const password = useRef("");
  const phone = useRef("");
  const parentEmail = useRef("");
  const navigate = useNavigate();

  const { isLoading, error } = useQuery("registerPageData", async () => {
    const resp = await axios.get(
      ApiEndpoint +
        "wp-json/firstchoiceprep/v1/getPageText?slug=" +
        pageIds.register,
    );
    setdata(resp.data as PageResponse);
  });

  const doRegister = async () => {
    setChecking(true);
    const checkEmail = validEmail.safeParse({ email: email.current });
    const checkParentEmail = validEmail.safeParse({
      email: parentEmail.current,
    });
    const checkPassword = notEmptyString.safeParse(password.current);
    const checkFirstName = notEmptyString.safeParse(password.current);
    const checkLastName = notEmptyString.safeParse(password.current);
    const checkPhone = validPhone.safeParse(phone.current);

    if (
      checkEmail.success &&
      checkPassword.success &&
      checkFirstName.success &&
      checkLastName.success &&
      checkParentEmail.success &&
      checkPhone.success
    ) {
      const resp = await axios.post(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/register",
        {
          email: email.current,
          password: password.current,
          first_name: firstName.current,
          last_name: lastName.current,
          phone: phone.current,
          parent_guardian_email: parentEmail.current,
        },
      );
      setChecking(false);
      if (resp.data) {
        const success: any = resp.data.success;
        if (success) {
          localStorage.setItem(
            "registrationData",
            JSON.stringify({
              userid: resp.data.userid,
              token: resp.data.token,
              email: email.current,
              phone: phone.current,
            })
          );
          navigate("/register-coupon", {
            replace: true,
            state: {
              userid: resp.data.userid,
              token: resp.data.token,
              userData: resp.data,
              username: email.current,
            },
          });
          // navigate("/register-confirm", { replace: true });
        } else {
          setErrorMsg(resp.data.message);
          setSnackError(true);
        }
      } else {
        setErrorMsg("Error Registering. Please Try Again.");
        setSnackError(true);
      }
    } else {
      setErrorMsg("Please fill in all appropriate fields before registering");
      setSnackError(true);
      setChecking(false);
    }
  };

  const closeSnack = () => {
    setSnackError(false);
  };

  useEffect(() => {
    const onEnter = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        doRegister();
      }
    };
    document.addEventListener("keydown", onEnter);
    return () => {
      document.removeEventListener("keydown", onEnter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <Loader />;
  if (error) return <LoadError />;

  return (
    <div className="registerPage">
      <BackButton navigate={navigate} destination="/" text="home" />
      <div className="headline">
        <h1>{data?.headerTitle}</h1>
        <div
          className="description"
          dangerouslySetInnerHTML={{ __html: data?.welcomeMessage }}
        />
      </div>
      <div className="loginForm">
        <div className="formRow">
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (parentEmail.current = evt.target.value)}
            id="filled-basic"
            label="Parent/Guardian Email"
            variant="filled"
          />
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (firstName.current = evt.target.value)}
            id="filled-basic"
            label="Student's First Name"
            variant="filled"
          />
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (lastName.current = evt.target.value)}
            id="filled-basic"
            label="Student's Last Name"
            variant="filled"
          />
        </div>
        <div className="formRow">
          <BrandInput
            autoComplete="no"
            value={phoneValue}
            onChange={(evt) => {
              const digits = evt.target.value.replace(/\D/g, "");
              phone.current = digits;
              setPhoneValue(digits);
            }}
            id="filled-basic"
            label="Student's Phone"
            variant="filled"
            inputProps={{ inputMode: "numeric" }}
            placeholder="Numbers only"
          />
          <BrandInput
            autoComplete="no"
            onChange={(evt) => (email.current = evt.target.value)}
            id="filled-basic"
            label="Student's Email"
            variant="filled"
          />
          <BrandInput
            id="filled-basic"
            label="Student's Password"
            type="password"
            variant="filled"
            autoComplete="no"
            onChange={(evt) => (password.current = evt.target.value)}
          />
        </div>
        <div className="formRow">
          {checking ? (
            <FontAwesomeIcon icon={faSpinner} />
          ) : (
            <BrandButton onClick={doRegister}>REGISTER</BrandButton>
          )}
        </div>
      </div>
      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={closeSnack}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">{errorMsg}</Alert>
      </Snackbar>
    </div>
  );
};

export default Register;
